import type { ItemLaborView } from '@obraplan/core/application/contracts/purchase-lines';
import type { ScheduleItemView } from '@obraplan/core/application/contracts/schedule-items';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useContainer, useSessionState } from '@/features/session/use-session';
import { formatBusinessDate } from '@/lib/format';
import { useLive } from '@/lib/use-live';
import { ExecucaoDaFrente } from '@/features/room-progress/ExecucaoDaFrente';
import { ItemDetails, type ItemMaterials, type Loadable } from './ItemDetails';

export interface ItemDetailsDialogProps {
  /** O item aberto; `undefined` fecha o diálogo. */
  readonly item: ScheduleItemView | undefined;
  readonly onOpenChange: (open: boolean) => void;
}

/** O limite da página da lista de compra (`listPurchaseLinesSchema`). */
const LINES_PAGE_SIZE = 500;

/**
 * A execução, os materiais e a mão de obra de um item do cronograma por item (Fases 26 e 34).
 *
 * Lê duas coisas do servidor — as linhas de compra da frente e do serviço do item
 * (`ListPurchaseLines`) e o HH por função (`ListItemLabor`), as duas com `schedule.view`, a
 * mesma permissão da tela — e entrega o resultado ao painel. Cada resposta leva o id do item
 * que a pediu: ao abrir outro item, a resposta do anterior vale como "carregando", e não como
 * dado do item novo.
 *
 * O item "frente inteira" não tem serviço, e a lista não filtra por serviço nulo: pede-se a
 * frente e a tela fica com as linhas sem serviço.
 */
export function ItemDetailsDialog({ item, onOpenChange }: ItemDetailsDialogProps) {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();

  const materialsAnswer = useLive(
    () =>
      item === undefined || session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.listPurchaseLines
            .execute(
              {
                projectId: activeProjectId,
                floorId: item.floorId,
                disciplineId: item.disciplineId,
                ...(item.serviceId === null ? {} : { serviceId: item.serviceId }),
                page: 1,
                pageSize: LINES_PAGE_SIZE,
              },
              session,
            )
            .then((result) => ({ itemId: item.id, result })),
    [item?.id, session?.userId, activeProjectId],
  );

  const laborAnswer = useLive(
    () =>
      item === undefined || session === undefined || activeProjectId === undefined
        ? Promise.resolve(undefined)
        : container.listItemLabor
            .execute({ projectId: activeProjectId, itemId: item.id }, session)
            .then((result) => ({ itemId: item.id, result })),
    [item?.id, session?.userId, activeProjectId],
  );

  const materials: Loadable<ItemMaterials> =
    item === undefined || materialsAnswer === undefined || materialsAnswer.itemId !== item.id
      ? { state: 'loading' }
      : !materialsAnswer.result.ok
        ? { state: 'error', message: materialsAnswer.result.error.message }
        : {
            state: 'ready',
            value: {
              lines:
                item.serviceId === null
                  ? materialsAnswer.result.value.items.filter((line) => line.serviceId === null)
                  : materialsAnswer.result.value.items,
              truncated:
                materialsAnswer.result.value.total > materialsAnswer.result.value.items.length,
            },
          };

  const labor: Loadable<readonly ItemLaborView[]> =
    item === undefined || laborAnswer === undefined || laborAnswer.itemId !== item.id
      ? { state: 'loading' }
      : !laborAnswer.result.ok
        ? { state: 'error', message: laborAnswer.result.error.message }
        : { state: 'ready', value: laborAnswer.result.value };

  const title =
    item === undefined
      ? 'Item'
      : `${item.disciplineName}${item.serviceName === null ? ' (frente inteira)' : ` · ${item.serviceName}`}`;

  return (
    <Dialog open={item !== undefined} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {item === undefined
              ? ''
              : `${item.towerName} · ${item.floorName}${item.segment === null ? '' : ` · ${item.segment}`} · ${formatBusinessDate(item.plannedStart)} a ${formatBusinessDate(item.plannedEnd)}`}
          </DialogDescription>
        </DialogHeader>

        {item !== undefined && item.serviceId !== null && (
          // Abaixo do item, sem datas próprias (Fase 34, D2): os cômodos ou as peças do pavimento
          // para o serviço, com a planta pintada (D3, D14).
          <section aria-labelledby="item-execution" className="flex flex-col gap-2">
            <div>
              <h3 id="item-execution" className="text-sm font-medium">
                Execução por cômodo ou peça
              </h3>
              <p className="text-xs text-(--color-content-muted)">
                O andamento abaixo do item, do pavimento inteiro
                {item.segment === null ? '' : ', não só deste trecho'}. A planta fica na Execução
                por cômodo, onde se marca em tela cheia; o apontamento do dia também lança.
              </p>
            </div>
            <ExecucaoDaFrente
              activityId={item.activityId}
              serviceId={item.serviceId}
              label={`${item.serviceName ?? ''} · ${item.floorName}`}
              unit={item.unit ?? undefined}
              planta="atalho"
            />
          </section>
        )}

        {item !== undefined && <ItemDetails item={item} materials={materials} labor={labor} />}

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
